import { ErrorLogLevel } from '@repo/database/browser';
import type {
  ErrorLogStats,
  FetchErrorLogsGroupedParams,
  FetchErrorLogsParams,
} from '@/api/error-logs';
import {
  type ApiParamReaders,
  pickApiParams,
  SEARCH_AND_SORT_PARAMS,
  valueParam,
} from '@/lib/helpers/list-api-params';
import type { TabCounts, TranslatableDomainTab } from '@/lib/helpers/list-tabs';
import type { ErrorLogsSearch } from '@/routes/-shared/errorLogsSearchSchema';

export type ViewType = 'table' | 'progressive';

/**
 * Il tab di partenza: i critici da risolvere. Quando non ce ne sono, lo stato
 * vuoto della tabella propone «Mostra tutti».
 */
export const ERROR_LOGS_DEFAULT_TAB = 'critical';
export const ERROR_LOGS_ALL_TAB = 'all';

/**
 * Tab della tabella sul livello, nell'ordine del design (i link vecchi con
 * `?level=` li traduce lo schema). Critici ed Errori hanno il contatore dei
 * da risolvere, rosso come il badge del loro livello; gli Avvisi no, non
 * chiedono un intervento.
 */
export const ERROR_LOGS_TABS: readonly TranslatableDomainTab<ErrorLogLevel>[] =
  [
    { value: ERROR_LOGS_ALL_TAB, text: 'errorLogs.tabs.all' },
    {
      value: ERROR_LOGS_DEFAULT_TAB,
      text: 'errorLogs.tabs.critical',
      filter: ErrorLogLevel.CRITICAL,
      countTone: 'danger',
    },
    {
      value: 'error',
      text: 'errorLogs.tabs.error',
      filter: ErrorLogLevel.ERROR,
      countTone: 'danger',
    },
    {
      value: 'warning',
      text: 'errorLogs.tabs.warning',
      filter: ErrorLogLevel.WARN,
    },
  ];

/**
 * Contatori dei tab dalle statistiche (solo errori da risolvere): Critici ed
 * Errori. Senza statistiche nessun contatore, invece di uno 0 provvisorio.
 */
export function buildErrorLogTabCounts(
  stats: Pick<ErrorLogStats, 'byLevel'> | undefined
): TabCounts | undefined {
  if (!stats) return undefined;
  return {
    [ERROR_LOGS_DEFAULT_TAB]: stats.byLevel[ErrorLogLevel.CRITICAL] ?? 0,
    error: stats.byLevel[ErrorLogLevel.ERROR] ?? 0,
  };
}

/** I parametri che il cambio vista porta con sé. */
type ViewSearch = Partial<
  Pick<ErrorLogsSearch, 'viewType' | 'includeResolved' | 'expanded' | 'subTab'>
>;

export function resolveViewType(search: ViewSearch): ViewType {
  return search.viewType ?? 'table';
}

/**
 * Campi della vista progressiva: solo ciò che ha a video (l'interruttore
 * «Mostra risolti»); azienda e periodo sono filtri della tabella e il cambio
 * vista li scarta.
 */
const GROUPED_ERROR_LOGS_API_PARAMS: ApiParamReaders<
  ErrorLogsSearch,
  FetchErrorLogsGroupedParams
> = { includeResolved: valueParam };

type ErrorLogsApiParams = Omit<FetchErrorLogsParams, 'skip' | 'take'>;

/** Campi di `GET backoffice/error-logs`; il livello arriva dal tab. */
const ERROR_LOGS_API_PARAMS: ApiParamReaders<
  ErrorLogsSearch,
  ErrorLogsApiParams
> = {
  ...SEARCH_AND_SORT_PARAMS,
  includeResolved: valueParam,
  categoryName: valueParam,
  tenantId: valueParam,
  from: valueParam,
  to: valueParam,
};

export function buildErrorLogsApiParams(
  search: ErrorLogsSearch,
  level: ErrorLogLevel | undefined
): ErrorLogsApiParams {
  return { ...pickApiParams(search, ERROR_LOGS_API_PARAMS), level };
}

export function buildGroupedErrorLogsApiParams(
  search: ErrorLogsSearch
): FetchErrorLogsGroupedParams {
  return pickApiParams(search, GROUPED_ERROR_LOGS_API_PARAMS);
}

/**
 * La search dopo il cambio vista: si tengono solo i parametri che la vista
 * di arrivo sa leggere, gli altri resterebbero nell'URL senza un controllo.
 * Il tab (`subTab`) resta in entrambe le direzioni: tornando alla tabella si
 * ritrova il livello che si stava guardando.
 */
export function toViewSearch(
  previous: ViewSearch,
  viewType: ViewType
): ViewSearch {
  const shared = {
    viewType,
    includeResolved: previous.includeResolved,
    subTab: previous.subTab,
  };
  return viewType === 'progressive'
    ? { ...shared, expanded: previous.expanded }
    : shared;
}
